import { dialogData } from '../store/ErrorMessage/ErrorMessageSlice';

export function alignerPrescriptionFormValidation(
    {
        attachments,
        spacingAndCrowding,
        openbite,
        deepBite,
        midline,
        posteriorCrossbite,
        auxiliaries,
        restorativeOptions,
        preImplantAligment,
        archToTreat,
        anteriorPosteriorRelationship,
    },
    value
) {
    // value variable is for Prescription Notes tab (tab in new case).
    // - If value is NOT provided, the helper is being called from the Next-button
    //   flow (NewPatientButtonTabs.handleChangeTab). All conditions must be
    //   evaluated so the "Unable to Proceed" popup can guard tab navigation.
    // - If value IS provided, the user is editing a field via onChange. Required-
    //   field conditions must NOT fire here:
    //     * Validating other fields would pop the popup on unrelated clicks
    //     * Validating the same field would pop the popup while the user is
    //       trying to fill the very input that resolves the condition (e.g.
    //       typing into Q6's location field, or clicking attachment teeth).
    //   So required-field conditions are only evaluated on Next.
    const shouldValidateField = () => !value;
    const {
        condition1,
        condition3,
        condition4,
        condition5,
        condition6,
        condition7,
        condition8,
        condition9,
        condition10,
        condition11,
        condition12,
        condition13,
        condition14,
        condition16,
        condition17,
        condition18,
        condition19,
        condition20,
        condition21,
        condition22,
        // restorativeOptionsSubValueErr,
        restorativeOptionsSubValueOnlayErr,
        restorativeOptionsVerticalDimensionUpperPosteriorErr,
        restorativeOptionsVerticalDimensionLowerPosteriorErr,
        preImplantAligmentProstheticSpaceErr,
    } = checkFormFields({
        attachments,
        spacingAndCrowding,
        openbite,
        deepBite,
        midline,
        posteriorCrossbite,
        auxiliaries,
        restorativeOptions,
        preImplantAligment,
        archToTreat,
        anteriorPosteriorRelationship,
    });

    return {
        condition1: shouldValidateField('attachments') && condition1,
        condition3: shouldValidateField('attachments') && condition3,
        condition4: shouldValidateField('spacing') && condition4,
        condition5: shouldValidateField('openbite') && condition5,
        condition6: shouldValidateField('openbite') && condition6,
        condition7: shouldValidateField('openbite') && condition7,
        condition8: shouldValidateField('deepBite') && condition8,
        condition9: shouldValidateField('deepBite') && condition9,
        condition10: shouldValidateField('deepBite') && condition10,
        condition11: shouldValidateField('midline') && condition11,
        condition12: shouldValidateField('midline') && condition12,
        condition13: shouldValidateField('posteriorCrossbite') && condition13,
        condition14: shouldValidateField('auxiliaries') && condition14,
        condition16: shouldValidateField('posteriorCrossbite') && condition16,
        condition17: shouldValidateField('posteriorCrossbite') && condition17,
        // Only validate during tab navigation
        condition18: !value && condition18,
        condition19: !value && condition19,
        condition20: !value && condition20,
        condition21: !value && condition21,
        condition22: !value && condition22,
        // restorativeOptionsSubValueErr:
        //     shouldValidateField('restorativeOptionsSubValue') && restorativeOptionsSubValueErr,
        restorativeOptionsSubValueOnlayErr:
            shouldValidateField('restorativeOptionsSubValueOnlayPosition') && restorativeOptionsSubValueOnlayErr,
        restorativeOptionsVerticalDimensionUpperPosteriorErr:
            shouldValidateField('restorativeOptionsVerticalDimensionUpperPosterior') &&
            restorativeOptionsVerticalDimensionUpperPosteriorErr,
        restorativeOptionsVerticalDimensionLowerPosteriorErr:
            shouldValidateField('restorativeOptionsVerticalDimensionLowerPosterior') &&
            restorativeOptionsVerticalDimensionLowerPosteriorErr,

        preImplantAligmentProstheticSpaceErr:
            shouldValidateField('preImplantAligmentProstheticSpace') && preImplantAligmentProstheticSpaceErr,
    };
}

// Create a map for faster lookup
const conditionMap = new Map([
    ['condition1', { errorQuestion: 1, scrollId: 'Q3', dialogMessage: dialogData.tooth.content }],
    ['condition3', { errorQuestion: 3, scrollId: 'Q3', dialogMessage: dialogData.value.content }],
    ['condition4', { errorQuestion: 4, scrollId: 'Q6', dialogMessage: dialogData.locationOfTheSpace.content }],
    ['condition5', { errorQuestion: 5, scrollId: 'Q8', dialogMessage: dialogData.checkbox.content }],
    ['condition6', { errorQuestion: 6, scrollId: 'Q8', dialogMessage: dialogData.checkbox.content }],
    ['condition7', { errorQuestion: 7, scrollId: 'Q8', dialogMessage: dialogData.checkbox.content }],
    ['condition8', { errorQuestion: 8, scrollId: 'Q9', dialogMessage: dialogData.checkbox.content }],
    ['condition9', { errorQuestion: 9, scrollId: 'Q9', dialogMessage: dialogData.checkbox.content }],
    ['condition10', { errorQuestion: 10, scrollId: 'Q9', dialogMessage: dialogData.specifyArea.content }],
    ['condition11', { errorQuestion: 11, scrollId: 'Q10', dialogMessage: dialogData.option.content }],
    ['condition12', { errorQuestion: 12, scrollId: 'Q10', dialogMessage: dialogData.option.content }],
    ['condition13', { errorQuestion: 13, scrollId: 'Q11', dialogMessage: dialogData.checkbox.content }],
    ['condition14', { errorQuestion: 14, scrollId: 'Q12', dialogMessage: dialogData.prescriptionNotes.content }],
    ['condition16', { errorQuestion: 16, scrollId: 'Q11', dialogMessage: dialogData.checkbox.content }],
    ['condition17', { errorQuestion: 17, scrollId: 'Q11', dialogMessage: dialogData.prescriptionNotes.content }],
    ['condition18', { errorQuestion: 18, scrollId: 'Q1', dialogMessage: dialogData.archToTreat.content }],
    ['condition19', { errorQuestion: 19, scrollId: 'Q3', dialogMessage: dialogData.attachmentsRequired.content }],

    [
        'condition20',
        { errorQuestion: 20, scrollId: 'Q5', dialogMessage: dialogData.anteriorPosteriorRelationship.content },
    ],
    ['condition21', { errorQuestion: 21, scrollId: 'Q6', dialogMessage: dialogData.spacingAndCrowding.content }],
    ['condition22', { errorQuestion: 22, scrollId: 'Q12', dialogMessage: dialogData.auxiliariesElastics.content }],
    // [
    //     'restorativeOptionsSubValueErr',
    //     {
    //         errorQuestion: undefined,
    //         scrollId: 'restorativeOptions',
    //         dialogMessage: dialogData.restorativeOptions.subValue,
    //     },
    // ],
    [
        'restorativeOptionsSubValueOnlayErr',
        { errorQuestion: undefined, scrollId: 'restorativeOptions', dialogMessage: dialogData.tooth.content },
    ],
    [
        'restorativeOptionsVerticalDimensionPosteriorErr',
        {
            errorQuestion: undefined,
            scrollId: 'restorativeOptions',
            dialogMessage: dialogData.restorativeOptions.posterior,
        },
    ],

    [
        'preImplantAligmentProstheticSpaceErr',
        { errorQuestion: undefined, scrollId: 'preImplantAligment', dialogMessage: dialogData.tooth.content },
    ],
]);

export function handleValidationOnTabsChange(conditions) {
    let response = {
        needHandle: false,
        errorQuestion: 0,
        scrollId: '',
        dialogMessage: '',
        dialogMessage: '',
    };
    // Find first matching condition
    for (const [conditionKey, config] of conditionMap) {
        if (conditions[conditionKey]) {
            response.needHandle = true;
            response.errorQuestion = config.errorQuestion;
            response.scrollId = config.scrollId;
            response.dialogMessage = config.dialogMessage;
            break;
        }
    }
    return response;
}

export const ERROR_CONDITIONS = new Set([
    'condition1',
    'condition3',
    'condition4',
    'condition5',
    'condition6',
    'condition7',
    'condition8',
    'condition9',
    'condition10',
    'condition11',
    'condition12',
    'condition13',
    'condition14',
    'condition16',
    'condition17',
    'condition18',
    'condition19',
    'condition20',
    'condition21',
    'condition22',
    // 'restorativeOptionsSubValueErr',
    'restorativeOptionsSubValueOnlayErr',
    // 'restorativeOptionsVerticalDimensionToothAreaErr',
    // 'restorativeOptionsVerticalDimensionJawPositionErr',
    // 'restorativeOptionsVerticalDimensionIncreaseErr',
    // 'restorativeOptionsVerticalDimensionUpperPosteriorErr',
    // 'restorativeOptionsVerticalDimensionLowerPosteriorErr',
    'restorativeOptionsVerticalDimensionPosteriorErr',
    'preImplantAligmentProstheticSpaceErr',
]);

export function isConditionsHavingErrors(conditions) {
    for (const conditionKey of ERROR_CONDITIONS) {
        if (conditions[conditionKey]) {
            return true; // Found error
        }
    }
    return false; // No errors found
}

export function validateFormFieldsOnChangeForTabsReminder({
    attachments,
    spacingAndCrowding,
    openbite,
    deepBite,
    midline,
    posteriorCrossbite,
    auxiliaries,
    restorativeOptions,
    preImplantAligment,
    archToTreat,
    anteriorPosteriorRelationship,
}) {
    const conditions = checkFormFields({
        attachments,
        spacingAndCrowding,
        openbite,
        deepBite,
        midline,
        posteriorCrossbite,
        auxiliaries,
        restorativeOptions,
        preImplantAligment,
        archToTreat,
        anteriorPosteriorRelationship,
    });
    return isConditionsHavingErrors(conditions);
}

export function checkFormFields({
    attachments,
    spacingAndCrowding,
    openbite,
    deepBite,
    midline,
    posteriorCrossbite,
    auxiliaries,
    restorativeOptions,
    preImplantAligment,
    archToTreat,
    anteriorPosteriorRelationship,
}) {
    // const restorativeOptionsSubValueErr = restorativeOptions?.value === 'Yes' && !restorativeOptions?.subValue?.value;
    const onlayPosition = Array.isArray(restorativeOptions?.subValue?.onlayPosition)
        ? restorativeOptions?.subValue?.onlayPosition
        : [];
    const restorativeOptionsSubValueOnlayErr = restorativeOptions?.value === 'Yes' && !onlayPosition?.length;
    const restorativeOptionsVerticalDimensionUpperPosteriorErr =
        restorativeOptions?.value === 'Yes' &&
        !restorativeOptions?.subValue?.verticalDimension?.verticalDimensionUpperPosterior;
    const restorativeOptionsVerticalDimensionLowerPosteriorErr =
        restorativeOptions?.value === 'Yes' &&
        !restorativeOptions?.subValue?.verticalDimension?.verticalDimensionLowerPosterior;

    const preImplantAligmentProstheticSpaceErr =
        preImplantAligment?.value === 'Yes' && !preImplantAligment?.prostheticSpace?.length;
    return {
        condition1:
            attachments.value === 'includedExcludedAttachments' &&
            attachments.doNotPlace.length === 0 &&
            attachments.place.length === 0,
        condition3: attachments.delay.value === 'delayToStage' && !attachments.delay.stage,
        condition4: spacingAndCrowding.spacing.value === 'leaveSpecificSpaces' && !spacingAndCrowding.spacing.location,
        condition5:
            openbite.subValue.extrudeAnteriorTeeth.value &&
            !openbite.subValue.extrudeAnteriorTeeth.upper &&
            !openbite.subValue.extrudeAnteriorTeeth.lower,
        condition6:
            openbite.subValue.intrudePosteriorTeeth.value &&
            !openbite.subValue.intrudePosteriorTeeth.upper &&
            !openbite.subValue.intrudePosteriorTeeth.lower,
        condition7: openbite.subValue.placeMiniImplantsTAD.value && !openbite.subValue.placeMiniImplantsTAD.input,
        condition8:
            deepBite.subValue.extrudePosteriorTeeth.value &&
            !deepBite.subValue.extrudePosteriorTeeth.upper &&
            !deepBite.subValue.extrudePosteriorTeeth.lower,
        condition9:
            deepBite.subValue.intrudeAnteriorTeeth.value &&
            !deepBite.subValue.intrudeAnteriorTeeth.upper &&
            !deepBite.subValue.intrudeAnteriorTeeth.lower,
        condition10: deepBite.subValue.placeMiniImplantsTAD.value && !deepBite.subValue.placeMiniImplantsTAD.input,
        condition11: midline.subValue.lower.checked && !midline.subValue.lower.value,
        condition12: midline.subValue.upper.checked && !midline.subValue.upper.value,
        condition13:
            posteriorCrossbite.subValue.value === 'interarchElastics' &&
            posteriorCrossbite.subValue.subValue.value === 'buttonPressedDirectlyOnAligner' &&
            !posteriorCrossbite.subValue.subValue.buttonPressedDirectlyOnAligner.mandibularTeeth &&
            !posteriorCrossbite.subValue.subValue.buttonPressedDirectlyOnAligner.maxillaryTeeth,
        condition14: auxiliaries.otherElastics.value === 'Yes' && !auxiliaries.otherElastics.comment,
        condition16:
            posteriorCrossbite.subValue.value === 'interarchElastics' &&
            posteriorCrossbite.subValue.subValue.value === 'cutoutToBondButtonDirectlyOnTeeth' &&
            !posteriorCrossbite.subValue.subValue.cutoutToBondButtonDirectlyOnTeeth.mandibularTeeth &&
            !posteriorCrossbite.subValue.subValue.cutoutToBondButtonDirectlyOnTeeth.maxillaryTeeth,
        condition17:
            posteriorCrossbite.subValue.value === 'otherMethods' && !posteriorCrossbite.subValue.subValue.otherMethod,
        condition18: archToTreat == null,
        condition19: attachments.value == null || attachments.delay.value == null,
        condition20:
            anteriorPosteriorRelationship.r == null ||
            anteriorPosteriorRelationship.l == null ||
            anteriorPosteriorRelationship.r === 'improveRelationship' ||
            anteriorPosteriorRelationship.l === 'improveRelationship',
        condition21:
            spacingAndCrowding.spacing.value == null ||
            Object.values(spacingAndCrowding.crowding.resolveUpper).some((field) => field.value == null) ||
            Object.values(spacingAndCrowding.crowding.resolveLower).some((field) => field.value == null),
        condition22:
            auxiliaries.classIIElastics == null ||
            auxiliaries.classIIIElastics == null ||
            auxiliaries.otherElastics.value == null,
        // restorativeOptionsSubValueErr,
        restorativeOptionsSubValueOnlayErr,
        // restorativeOptionsVerticalDimensionToothAreaErr,
        // restorativeOptionsVerticalDimensionJawPositionErr,
        // restorativeOptionsVerticalDimensionIncreaseErr,
        restorativeOptionsVerticalDimensionUpperPosteriorErr,
        restorativeOptionsVerticalDimensionLowerPosteriorErr,
        preImplantAligmentProstheticSpaceErr,
    };
}
